import { observer } from 'mobx-react-lite'
import { type JobDetailsViewModel } from '@/features/jobs/details/JobDetailsViewModel'
import { ContentLoading } from '@/components/ContentLoading'
import { ContentError } from '@/components/ContentError'
import { type QuestionEditorViewModel } from '@/features/jobs/questions/QuestionEditorViewModel'
import { ResponseValueContainer } from '@/features/jobs/questions/components/ResponseValueContainer'
import { match } from '@/utils/pattern-match-util'
import { CapturedInformationLoadContainer } from '@/features/jobs/details/components/CapturedInformationLoadContainer'
import { NotesTabContainer } from '@/features/jobs/details/components/CapturedInformationCard/NotesTabContainer'
import { type QuestionListViewModel } from '@/features/jobs/questions/QuestionListViewModel'
import { BusinessInfoContainer } from '@/features/jobs/info-gathering/business-info/components/BusinessInfo/BusinessInfoContainer'
import { PersonalProfileContainer } from '@/features/jobs/info-gathering/individual-info/components/PersonalProfile/PersonalProfileContainer'
import { SpousalProfileContainer } from '@/features/jobs/info-gathering/individual-info/components/SpousalProfile/SpousalProfileContainer'
import { DependentsProfileContainer } from '@/features/jobs/info-gathering/individual-info/components/DependentsProfile/DependentsProfileContainer'
import { RefundInfoContainer } from '@/features/jobs/info-gathering/tax-refund-method/components/RefundInfo/RefundInfoContainer'
import {
  QuestionsTab,
  Response,
  ResponsesCard,
} from '@/features/jobs/details/components/ResponsesCard'

interface ResponseContainerProps {
  questionVm: QuestionEditorViewModel
}

const ResponseContainer = observer(function ResponseContainer({
  questionVm,
}: ResponseContainerProps) {
  return (
    <Response label={questionVm.title}>
      <ResponseValueContainer editorVm={questionVm.editor} />
    </Response>
  )
})

interface QuestionsTabContainerProps {
  vm: QuestionListViewModel
}

const QuestionsTabContainer = observer(function QuestionsTabContainer({
  vm,
}: QuestionsTabContainerProps) {
  return (
    <QuestionsTab>
      {vm.questionVms.map((question) => (
        <ResponseContainer key={question.id} questionVm={question} />
      ))}
    </QuestionsTab>
  )
})

export interface ResponsesCardContainerProps {
  vm: JobDetailsViewModel
}

export const ResponsesCardContainer = observer(function ResponsesCardContainer({
  vm,
}: ResponsesCardContainerProps) {
  return (
    <ResponsesCard
      activeTab={vm.detailsVm.informationTabController.current.tab}
      questionCount={
        vm.detailsVm.responses.questionVms.length || vm.job.answers.length
      }
      showNotesTab={vm.capturedInformationVm.hasIntakeTab('Notes')}
      showPersonalTab={vm.capturedInformationVm.hasIntakeTab('PersonalInfo')}
      showBusinessTab={vm.capturedInformationVm.hasIntakeTab('BusinessInfo')}
      showRefundTab={vm.capturedInformationVm.hasIntakeTab('RefundInfo')}
      onTabChange={vm.detailsVm.informationTabController.change}
      personalInfoTabTestId="personal-info-tab-item"
      businessTabTestId="business-tab-item"
      refundTabTestId="refund-tab-item"
    >
      {match(vm.detailsVm.informationTabController.current.tab, {
        Questions: () =>
          vm.detailsVm.fetchResponses.match({
            pending: () =>
              vm.detailsVm.isRevalidatingResponses ? (
                <QuestionsTabContainer vm={vm.detailsVm.responses} />
              ) : (
                <ContentLoading size="section" />
              ),
            rejected: (err) => (
              <ContentError
                size="section"
                title="Error loading responses."
                error={err}
              />
            ),
            resolved: () => (
              <QuestionsTabContainer vm={vm.detailsVm.responses} />
            ),
          }),
        Notes: () => (
          <CapturedInformationLoadContainer vm={vm.capturedInformationVm}>
            <NotesTabContainer vm={vm.capturedInformationVm.notesVm} />
          </CapturedInformationLoadContainer>
        ),
        PersonalInfo: () => (
          <CapturedInformationLoadContainer vm={vm.capturedInformationVm}>
            {vm.capturedInformationVm.personalProfileVm && (
              <PersonalProfileContainer
                vm={vm.capturedInformationVm.personalProfileVm}
                editVm={vm.capturedInformationVm.editPersonalProfileDialogVm}
              />
            )}
            {vm.capturedInformationVm.spousalProfileVm && (
              <SpousalProfileContainer
                vm={vm.capturedInformationVm.spousalProfileVm}
                editVm={vm.capturedInformationVm.editPersonalProfileDialogVm}
              />
            )}
            {vm.capturedInformationVm.dependentProfileVm && (
              <DependentsProfileContainer
                vm={vm.capturedInformationVm.dependentProfileVm}
                editVm={vm.capturedInformationVm.editPersonalProfileDialogVm}
              />
            )}
          </CapturedInformationLoadContainer>
        ),
        BusinessInfo: () => (
          <CapturedInformationLoadContainer vm={vm.capturedInformationVm}>
            {vm.capturedInformationVm.businessInfoVm && (
              <BusinessInfoContainer
                vm={vm.capturedInformationVm.businessInfoVm}
                editVm={vm.capturedInformationVm.editBusinessProfileDialogVm}
              />
            )}
          </CapturedInformationLoadContainer>
        ),
        RefundInfo: () => (
          <CapturedInformationLoadContainer vm={vm.capturedInformationVm}>
            {vm.capturedInformationVm.taxRefundMethodVm && (
              <RefundInfoContainer
                vm={vm.capturedInformationVm.taxRefundMethodVm}
                editVm={vm.capturedInformationVm.editRefundDialogVm}
              />
            )}
          </CapturedInformationLoadContainer>
        ),
      })}
    </ResponsesCard>
  )
})
